import { useEffect, useRef, useState } from 'react'
import { LEDGER_CHANNELS } from '@marva/shared/ledger-vocabulary'
import type { ActivityFeedback } from '../../services/api'
import { FEEDBACK_CATEGORIES, type FeedbackCategory } from '../../lib/feedbackCategories'
import { Button, Field, Hint, Inline, RadioGroup, ReadingSection, Stack } from '../../ds'

/**
 * "Was this the right call?" — the v6 correction flow (MAR-316), in place of the old binary
 * thumbs: "Yes, do this again", or "No — let me correct it" → what should change (one of the
 * categories the feedback route accepts) → optionally what she should have done → "Record the
 * correction" → a settled line.
 *
 * ⚠️ Undo is CLIENT-SIDE, before the answer is sent. The route has no delete — a second answer
 * replaces the first (the row is keyed on the activity) — so a recorded answer cannot be taken
 * back, only changed. The caller therefore holds a settled answer for a short window
 * (`pending`) before it POSTs; Undo in that window drops it unsent. After it lands, the control
 * is "Change my answer", which records a new one over it.
 */

export type FeedbackState = {
  status: 'pending' | 'sending' | 'recorded' | 'error'
  payload: ActivityFeedback
}

/** Each category in the merchant's words. A `Record` over the route's own list, so a new one fails to compile. */
const FEEDBACK_CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  tone: 'The tone',
  timing: 'The timing',
  channel: 'The channel',
  content: 'What she said',
  discount: 'The offer',
  other: 'Something else',
}

const CATEGORY_OPTIONS = FEEDBACK_CATEGORIES.map((c) => ({ value: c, label: FEEDBACK_CATEGORY_LABELS[c] }))

/** "What would you have done instead?" — the route takes any string; this keeps it a sentence or two. */
const FEEDBACK_DETAIL_MAX = 1000

type Draft = {
  /** The entry this draft belongs to — scoped at read, so another decision opens clean. */
  for: string
  step: 'correct'
  category: FeedbackCategory | null
  detail: string
  channel: string
}

function settledLine(p: ActivityFeedback): string {
  if (p.feedbackType === 'up') return 'Noted — you’d have made the same call.'
  const what = p.feedbackCategory ? FEEDBACK_CATEGORY_LABELS[p.feedbackCategory].toLowerCase() : null
  return what ? `Correction noted: ${what}.` : 'Correction noted.'
}

export default function DecisionFeedback({
  entryId,
  state,
  channelLabel,
  onRecord,
  onUndo,
}: {
  entryId: string
  state?: FeedbackState
  channelLabel: (c: string) => string | null
  onRecord: (payload: ActivityFeedback) => void
  onUndo?: () => void
}) {
  const [draft, setDraft] = useState<Draft | null>(null)
  // "Change my answer" re-opens the question for THIS entry only.
  const [reopenedFor, setReopenedFor] = useState<string | null>(null)
  const d = draft && draft.for === entryId ? draft : null
  const reopened = !!state && reopenedFor === entryId
  const settled = state && !reopened ? state : null

  // ── Focus follows the answer (a11y) ──────────────────────────────────────────
  // Every step here unmounts the button that was just pressed ("Yes" → "Undo" → "Change my
  // answer" → the question again), which would drop focus to <body>. When the step changes
  // because of something she did here (`acted` — a click does not focus a button in every
  // browser, so focus alone cannot say) — or the button under her focus went away (Undo lapsing
  // into "Change my answer") — focus moves to the first control of the new step. Opening an entry
  // never steals focus; a change while she is working elsewhere (focus outside, not lost) doesn't.
  const regionRef = useRef<HTMLDivElement>(null)
  const acted = useRef(false)
  const step = settled ? `settled:${settled.status === 'pending' ? 'pending' : settled.status}` : d ? 'draft' : 'question'
  const lastStep = useRef<{ entryId: string; step: string } | null>(null)
  useEffect(() => {
    const prev = lastStep.current
    lastStep.current = { entryId, step }
    const byHer = acted.current
    acted.current = false
    if (!prev || prev.entryId !== entryId || prev.step === step) return
    const region = regionRef.current
    if (!region) return
    const active = document.activeElement
    const lost = !active || active === document.body
    if (!byHer && !lost && !region.contains(active)) return
    const target = region.querySelector<HTMLElement>('button:not([disabled]), input:not([disabled]), textarea, select')
    target?.focus()
  }, [entryId, step])

  const record = (payload: ActivityFeedback) => {
    setDraft(null)
    setReopenedFor(null)
    onRecord(payload)
  }

  let body: React.ReactNode
  if (settled) {
    body = (
      <>
        {settled.status === 'error' && <Hint role="alert">She couldn’t record that just now.</Hint>}
        <Inline>
          {settled.status === 'pending' && onUndo ? (
            <Button variant="ghost" size="sm" onClick={onUndo}>
              Undo
            </Button>
          ) : settled.status === 'error' ? (
            <Button variant="ghost" size="sm" onClick={() => record(settled.payload)}>
              Try again
            </Button>
          ) : (
            <Button
              variant="link"
              disabled={settled.status === 'sending'}
              onClick={() => setReopenedFor(entryId)}
            >
              Change my answer
            </Button>
          )}
        </Inline>
      </>
    )
  } else if (d) {
    const ready = !!d.category
    const submit = () => {
      if (!d.category) return
      const detail = d.detail.trim()
      record({
        feedbackType: 'down',
        feedbackCategory: d.category,
        ...(detail ? { feedbackDetail: detail } : {}),
        ...(d.category === 'channel' && d.channel ? { suggestedAlternative: { channel: d.channel } } : {}),
      })
    }
    // A Stack, not a fragment: the question, the fields and the actions each need their own
    // breathing room. The options run down, one per line — across, in a drawer, they wrapped
    // mid-row (MAR-412).
    body = (
      <Stack>
        <RadioGroup
          legend="What should she change?"
          options={CATEGORY_OPTIONS}
          value={d.category ?? undefined}
          onChange={(v) => setDraft({ ...d, category: v as FeedbackCategory })}
        />
        {d.category === 'channel' && (
          <Field
            as="select"
            label="Which channel instead?"
            optional
            value={d.channel}
            onChange={(e) => setDraft({ ...d, channel: (e.target as HTMLSelectElement).value })}
            options={[
              { value: '', label: 'No preference' },
              ...LEDGER_CHANNELS.map((c) => ({ value: c, label: channelLabel(c) ?? c })),
            ]}
          />
        )}
        <Field
          as="textarea"
          label="What would you have done instead?"
          optional
          rows={2}
          maxLength={FEEDBACK_DETAIL_MAX}
          value={d.detail}
          onChange={(e) => setDraft({ ...d, detail: (e.target as HTMLTextAreaElement).value })}
        />
        <Inline>
          <Button variant="gold" size="sm" disabled={!ready} onClick={submit}>
            Record the correction
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>
            Cancel
          </Button>
        </Inline>
      </Stack>
    )
  } else {
    body = (
      <Inline>
        <Button variant="sage" size="sm" onClick={() => record({ feedbackType: 'up' })}>
          Yes, do this again
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setDraft({ for: entryId, step: 'correct', category: null, detail: '', channel: '' })}
        >
          No — let me correct it
        </Button>
        {reopened && (
          // "Change my answer" is a question, not a commitment: her recorded answer stands.
          <Button variant="link" onClick={() => setReopenedFor(null)}>
            Keep my answer
          </Button>
        )}
      </Inline>
    )
  }

  return (
    <ReadingSection label="Was this the right call?" testId="drawer-feedback">
      <div
        ref={regionRef}
        onClickCapture={(e) => {
          if ((e.target as Element).closest('button')) acted.current = true
        }}
      >
        {/* A live region that is always in the tree, so the settled line is announced when it
            appears — not only when it changes. */}
        <div role="status" aria-live="polite">
          {settled && (
            <p className="mv-prose" data-testid="feedback-settled">
              {settledLine(settled.payload)}
            </p>
          )}
        </div>
        {body}
      </div>
    </ReadingSection>
  )
}
